import { Alert, Inline, Stack, Text } from 'braid-design-system';
import { FormProvider, useForm } from 'react-hook-form';
import { TextField } from './components/TextField.tsx';
import { Email } from './components/Email.tsx';
import { Phone } from './components/Phone.tsx';
import { Url } from './components/Url.tsx';
import { TextArea } from './components/TextArea.tsx';
import { DateField } from './components/Dates/DateField.tsx';
import { Select } from './components/Select.tsx';
import { MultiSelect } from './components/MultiSelect.tsx';
import { NumberField } from './components/NumberField.tsx';

import { useLocalStorage } from '#src/hooks/storage.ts';

import { formConfig } from './formConfig.ts';
import { FormSchema, type FormValues } from './types.ts';
import { useEffect, useState } from 'react';
import { Submit } from './components/Submit.tsx';
import { ClearForm } from './components/Clear.tsx';
import { SaveFormValues } from './components/SaveFormValues.tsx';
import { evaluateDependencies } from './evaluateDependencies.ts';
import { safeParse } from '@seek/indie-api-types';

export const defaultValues: FormValues = Object.fromEntries(
  formConfig.map((config) => [config.id, config.initialValue]),
);

// TODO: Send the data somewhere
const submit = (_values: FormSchema): Promise<void> => Promise.resolve();

export const ContactForm = () => {
  // TODO: Remove this when the form is ready
  // It is temporary to prevent the form from being discovered, as /contact is not that obscure and hidden pages are still discoverable via search.
  const [renderForm] = useLocalStorage<boolean | null>(
    'seek-developer/render-contact-form',
    null,
  );
  useEffect(() => {
    if (!renderForm) {
      window.location.href =
        'https://employer.seek.com/partners/integration-request/';
    }
  }, [renderForm]);

  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [, setIsSubmitting] = useState(false);

  const [localFormValues, setLocalFormValues] =
    useLocalStorage<FormValues | null>('/seek-developer/contact-form', null);
  let values;
  try {
    values = localFormValues ?? defaultValues;
  } catch {
    setLocalFormValues(null);
    values = defaultValues;
  }

  const methods = useForm<FormValues>({
    values,
    reValidateMode: 'onChange',
  });
  const { handleSubmit, getValues, watch, setError } = methods;

  const onValid = async () => {
    setSubmissionError(null);
    setIsSubmitting(true);

    const formValues = getValues();

    const validValues = safeParse(formValues, FormSchema);
    if (!validValues.success) {
      Object.entries(validValues.errors).forEach(([key, value]) => {
        setError(key, {
          type: 'manual',
          message: value.message,
        });
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await submit(validValues.value);
    } catch (error) {
      if (error instanceof Error) {
        setSubmissionError(error.message);
      } else {
        setSubmissionError('An unknown error occurred during submission.');
      }
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <FormProvider {...methods}>
        <Stack space="large">
          {submissionError ? (
            <Alert tone="critical" id="form-submission-error">
              <Text>{submissionError}</Text>
            </Alert>
          ) : null}
          <SaveFormValues>
            {formConfig.map((element) => {
              if (!evaluateDependencies(element.dependsOn, watch)) {
                return null;
              }
              switch (element.inputType) {
                case 'TextField':
                  return <TextField key={element.id} element={element} />;
                case 'Email':
                  return <Email key={element.id} element={element} />;
                case 'Phone':
                  return <Phone key={element.id} element={element} />;
                case 'Url':
                  return <Url key={element.id} element={element} />;
                case 'TextArea':
                  return <TextArea key={element.id} element={element} />;
                case 'Date':
                  return <DateField key={element.id} element={element} />;
                case 'Select':
                  return <Select key={element.id} element={element} />;
                case 'MultiSelect':
                  return <MultiSelect key={element.id} element={element} />;
                case 'Number':
                  return <NumberField key={element.id} element={element} />;
                default:
                  throw new Error(`Unsupported input type: ${element}`);
              }
            })}
          </SaveFormValues>
          <Inline space="small">
            <Submit />
            <ClearForm />
          </Inline>
        </Stack>
      </FormProvider>
    </form>
  );
};
